import { RoleFunction } from '@eagle/common';
import { GetNextStagesResponse, LifecycleTemplate, Thing, ThingLifeCycleStateResponse } from '@eagle/core-data-types';
import { Button, Menu, MenuItem, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { FC, MouseEventHandler, ReactNode, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { useFetchOneCache, usePromise, usePromiseConditional } from '../../hooks';
import { CacheDataTypes, Nullable, Undefinable } from '../../types';
import { testid, useHasAuthorization } from '../../util';
import { StageConfirmDialog } from './stage-confirm-dialog';

interface Props {
  'data-testid'?: string;
  renderButton?: (params: { disabled?: boolean; onClick: MouseEventHandler<HTMLButtonElement> }) => ReactNode;
  sx?: SxProps;
  thing?: Thing;
  onSuccess?: (hasDeleteAction: boolean) => void;
}

const THING_ADMINISTRATOR = [RoleFunction.THING_ADMINISTRATOR] as const;

export const StageSelectionMenu: FC<Props> = ({ sx, thing, renderButton, onSuccess, ...props }) => {
  const { axios, restClient } = useAuthenticated();
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const { t } = useTranslation(['common', 'admin']);
  const [selectedStageId, setSelectedStageId] = useState<Nullable<string>>(null);
  const lifecycleTemplateCache = useFetchOneCache(CacheDataTypes.LIFECYCLE_TEMPLATE);
  const isOpen = Boolean(anchorEl);
  const { hasAuthorization } = useHasAuthorization();
  const readonly = !hasAuthorization(THING_ADMINISTRATOR);
  const disabled = !thing;

  const sharedThingId = thing?.sharedThingId;

  const [lifeCycleState] = usePromiseConditional<ThingLifeCycleStateResponse | undefined>(async () => {
    if (!thing?._id) return undefined;
    return restClient.thing.getCurrentLifecycleState(thing._id);
  }, () => isOpen, [axios, thing, isOpen]);

  const [lifecycleTemplate] = usePromise<Undefinable<LifecycleTemplate>>(async () => {
    const lifecycleTemplateId = lifeCycleState?.lifecycleTemplateId;
    if (!lifecycleTemplateId) return;
    return lifecycleTemplateCache.one(lifecycleTemplateId);
  }, [lifecycleTemplateCache, lifeCycleState]);

  const [stages, , stagesStatus] = usePromiseConditional<GetNextStagesResponse | null>(async () => {
    if (!sharedThingId) return null;
    const nextStages = await restClient.sharedThing.getNextStages(sharedThingId);
    return nextStages;
  }, () => isOpen, [restClient, sharedThingId, isOpen]);

  const [stakeholderRoles] = usePromiseConditional<string[] | null>(async () => {
    const lifecycleTemplateId = lifeCycleState?.lifecycleTemplateId;
    if (!lifecycleTemplateId) return null;
    return restClient.my.lifeCycleTemplate.getStakeholderRoles(lifecycleTemplateId);
  }, () => isOpen, [restClient, lifeCycleState, isOpen]);

  const onButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const renderMenuItems = (): JSX.Element[] | JSX.Element => {
    if (stagesStatus === 'pending' || !stakeholderRoles) {
      return <Typography px={2} py="6px">{t('common:common.labels.loading')}</Typography>;
    }

    if (!stages || stages.length === 0 || stagesStatus !== 'resolved') {
      return <Typography px={2} py="6px">{t('admin:page.thing-details.update-stage.hint.no-stage')}</Typography>;
    }

    return stages.map((stage, i) => (
      <MenuItem data-testid={testid`update-stage-menu-item-${stage.stageId}`} key={i} onClick={() => {
        setAnchorEl(null);
        setSelectedStageId(stage.stageId);
      }}>
        {stage.display}
      </MenuItem>
    ));
  };

  return <>
    <Tooltip title={readonly && t('common:page.thing-details.update-stage.hint.no-permission')}>
      <Box data-testid={props['data-testid']} sx={sx}>
        {renderButton
          ? renderButton({ disabled: readonly || disabled, onClick: onButtonClick })
          : <Button data-testid="update-stage-button" disabled={readonly || disabled} onClick={onButtonClick}>
            {t('admin:page.thing-detail.update-stage.action')}
          </Button>
        }
      </Box>
    </Tooltip>
    <Menu
      anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      open={!!anchorEl}
    >
      {renderMenuItems()}
    </Menu>
    {lifeCycleState && !!sharedThingId && lifecycleTemplate && stakeholderRoles && stages?.map((stage) => (
      <StageConfirmDialog
        lifecycleTemplate={lifecycleTemplate}
        key={stage.stageId}
        open={selectedStageId === stage.stageId}
        onClose={() => {
          setSelectedStageId(null);
        }}
        onSuccess={onSuccess}
        sharedThingId={sharedThingId}
        lifeCycleState={lifeCycleState}
        stakeholderRoles={stakeholderRoles}
        stageId={stage.stageId}
        editableStakeholders={stage.editableStakeholders}
        thing={thing}
      />
    ))}
  </>;
};
